import { MealDomainService } from './domain';
import { MealFactory } from './entity/mealFactory';

import type { MealIRepository } from './entity/mealIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { MealJson, MealJsonPvt } from '@/types/domain/meal/entities/mealEntity';

/**
 * 食事アプリケーションサービス
 */
export class MealApplicationService {
  private service!: MealDomainService;

  private mealFactory = new MealFactory();

  /**
   * Injection
   * @param mealRepository mealRepository
   * @param logger logger
   */
  public inject(
    mealRepository: MealIRepository,
    logger: Logger,
  ) {
    this.service = new MealDomainService(logger);
    this.service.inject(mealRepository);
  }

  /**
   * 食事リスト取得
   * @param token トークン
   * @returns リスト
   */
  async getMealList(token: string | null) {
    const result = await this.service.getMealList(token);
    return result;
  }

  /**
   * 指定日の食事リスト取得
   * @param date 日付
   * @returns リスト
   */
  async getMealListWithDate(date: string) {
    const result = await this.service.getMealListWithDate(date);
    return result;
  }

  /**
   * 食事作成
   * @param input 入力
   * @returns 結果
   */
  async createMeal(
    input: MealJson,
  ) {
    const entity = this.mealFactory.createFromJson(input);
    const result = await this.service.createMeal(entity);
    return result;
  }

  /**
   * 食事更新
   * @param input 入力
   * @returns 結果
   */
  async updateMeal(
    input: MealJson,
  ) {
    const entity = this.mealFactory.createFromJson(input);
    const result = await this.service.updateMeal(entity);
    return result;
  }

  /**
   * 食事削除
   * @param input 入力
   * @returns 結果
   */
  async deleteMeal(
    input: MealJson,
  ) {
    const entity = this.mealFactory.createFromJson(input);
    const result = await this.service.deleteMeal(entity);
    return result;
  }

  /**
   * 食事リストを更新する(Create)
   * @param mealList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createMealStore(
    mealList: MealJson[],
    payload: EntitySubscribeType,
    filter: ((json: MealJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      mealList,
      payload,
      this.mealFactory,
      filter,
    );
  }

  /**
   * 食事リストを更新する(Update)
   * @param mealList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateMealStore(
    mealList: MealJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      mealList,
      payload,
      this.mealFactory,
    );
  }

  /**
   * 食事リストを更新する(Delete)
   * @param mealList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteMealStore(
    mealList: MealJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      mealList,
      payload,
      this.mealFactory,
    );
  }
}
